import {
    ChartSignature,
    ChartSignatureElement,
    InterpretationSignal,
    SajuCalculationResult
} from './types';

export const CHART_SIGNATURE_VERSION = 'chart-signature-0.1.0';

const ELEMENT_LABELS = ['목', '화', '토', '금', '수'];

export function generateChartSignature(
    result: SajuCalculationResult,
    signals: InterpretationSignal[] = result.interpretationSignals || result.validation?.interpretationSignals || []
): ChartSignature {
    let pillars = result.pillars || result.chart?.pillars || [];
    let dayPillar = pillars[2] || {};
    let monthPillar = pillars[1] || {};
    let elements = normalizeElements(result.elements || result.validation?.elementDistribution || []);
    let excessiveElements = excessiveElementEntries(elements);
    let deficientElements = deficientElementEntries(elements);
    let centerTenGods = centerTenGodEntries(result, signals);
    let strength = strengthEntry(signals);
    let coreInteractions = coreInteractionEntries(result);
    let majorStars = majorStarEntries(result);
    let majorLuck = majorLuckEntry(result);
    let timeUnknown = !!(result.normalizedInput?.timeUnknown || result.validation?.normalizedInput?.timeUnknown || result.calculationOptions?.timeUnknown);

    let parts = {
        dayMaster: textValue(dayPillar.stem?.name),
        dayPillar: textValue(dayPillar.label),
        monthBranch: textValue(monthPillar.branch?.name),
        centerTenGods: centerTenGods,
        excessiveElements: excessiveElements,
        deficientElements: deficientElements,
        strength: strength,
        coreInteractions: coreInteractions,
        majorStars: majorStars,
        majorLuck: majorLuck,
        timeUnknown: timeUnknown
    };
    let signature = [
        `DM=${parts.dayMaster}`,
        `DP=${parts.dayPillar}`,
        `MB=${parts.monthBranch}`,
        `TG=${parts.centerTenGods.length ? parts.centerTenGods.join(',') : 'none'}`,
        `E+=${formatElements(parts.excessiveElements)}`,
        `E-=${formatElements(parts.deficientElements)}`,
        `STR=${parts.strength.label}${parts.strength.score !== undefined ? `:${parts.strength.score}` : ''}`,
        `REL=${parts.coreInteractions.length ? parts.coreInteractions.join(';') : 'none'}`,
        `STAR=${parts.majorStars.length ? parts.majorStars.join(';') : 'none'}`,
        `LUCK=${parts.majorLuck ? `${parts.majorLuck.direction}:${parts.majorLuck.startAge}:${parts.majorLuck.basisTerm}:${parts.majorLuck.firstFlow || 'none'}` : 'none'}`,
        `TU=${parts.timeUnknown ? '1' : '0'}`
    ].join('|');

    return {
        version: CHART_SIGNATURE_VERSION,
        key: `csig_${stableHash(signature)}`,
        signature: signature,
        parts: parts,
        evidenceIds: evidenceIds(parts)
    };
}

function centerTenGodEntries(result: SajuCalculationResult, signals: InterpretationSignal[]) {
    let fromSignal = findSignal(signals, 'sig-dominant-ten-god')?.meta as any;
    if (fromSignal?.dominantTenGod) return [String(fromSignal.dominantTenGod)];

    let tenGods = (result.tenGods || result.validation?.tenGods || [])
        .filter((item: any) => item?.label && item.label !== '일간' && Number(item.value || 0) > 0)
        .sort((a: any, b: any) => {
            let valueDiff = Number(b.value || 0) - Number(a.value || 0);
            if (valueDiff !== 0) return valueDiff;
            let percentDiff = Number(b.percent || 0) - Number(a.percent || 0);
            if (percentDiff !== 0) return percentDiff;
            return String(a.label).localeCompare(String(b.label));
        });
    if (!tenGods.length) return [];

    let topValue = Number(tenGods[0].value || 0);
    return unique(tenGods
        .filter((item: any) => Number(item.value || 0) === topValue)
        .map((item: any) => String(item.label))
        .sort());
}

function excessiveElementEntries(elements: ChartSignatureElement[]) {
    if (!elements.length) return [];
    let max = elements.slice().sort((a: any, b: any) => b.value - a.value)[0];
    return elements
        .filter((item: ChartSignatureElement) => item.value === max.value || item.percent >= 28)
        .sort(elementSort);
}

function deficientElementEntries(elements: ChartSignatureElement[]) {
    if (!elements.length) return [];
    let sorted = elements.slice().sort((a: any, b: any) => b.value - a.value);
    let max = sorted[0];
    let min = sorted[sorted.length - 1];
    let weak = elements.filter((item: ChartSignatureElement) => item.value === 0 || item.percent <= 10);
    if (!weak.length && min && max && min.value < max.value * 0.45) weak = [min];
    return weak.sort(elementSort);
}

function strengthEntry(signals: InterpretationSignal[]) {
    let meta = findSignal(signals, 'sig-strength-balance')?.meta as any;
    return {
        label: textValue(meta?.label),
        score: numberOrUndefined(meta?.score)
    };
}

function coreInteractionEntries(result: SajuCalculationResult) {
    return unique((result.interactions || result.validation?.interactions || [])
        .filter((section: any) => section?.active && section.label !== '궁성' && section.items?.length)
        .flatMap((section: any) => section.items.map((item: string) => `${section.label}:${item}`)))
        .slice(0, 8);
}

function majorStarEntries(result: SajuCalculationResult) {
    let noble = (result.nobleStars || result.validation?.nobleStars || []).map((star: any) => starEntry('귀인', star));
    let special = (result.specialStars || result.validation?.specialStars || []).map((star: any) => starEntry('신살', star));
    return unique([...noble, ...special].filter((item: string) => !!item)).slice(0, 8);
}

function majorLuckEntry(result: SajuCalculationResult) {
    let luck = result.luckInfo || result.validation?.majorLuck;
    if (!luck) return undefined;
    return {
        direction: textValue(luck.direction),
        startAge: Number(luck.startAge || 0),
        basisTerm: textValue(luck.basisTerm),
        firstFlow: result.luckFlow?.[0]?.pillar ? String(result.luckFlow[0].pillar) : undefined
    };
}

function normalizeElements(elements: any[]): ChartSignatureElement[] {
    let byLabel: any = {};
    for (let item of elements || []) {
        if (!item?.label) continue;
        byLabel[item.label] = {
            label: String(item.label),
            value: roundOne(Number(item.value || 0)),
            percent: Math.round(Number(item.percent || 0))
        };
    }
    for (let label of ELEMENT_LABELS) {
        if (!byLabel[label]) byLabel[label] = { label: label, value: 0, percent: 0 };
    }
    return ELEMENT_LABELS.map((label: string) => byLabel[label]);
}

function evidenceIds(parts: any) {
    return unique([
        `dayMaster:${parts.dayMaster}`,
        `pillar:day:${parts.dayPillar}`,
        `monthBranch:${parts.monthBranch}`,
        ...parts.centerTenGods.map((tenGod: string) => `tenGod:center:${tenGod}`),
        ...parts.excessiveElements.map((element: ChartSignatureElement) => `element:excessive:${element.label}:${element.value}:${element.percent}`),
        ...parts.deficientElements.map((element: ChartSignatureElement) => `element:deficient:${element.label}:${element.value}:${element.percent}`),
        `strength:${parts.strength.label}${parts.strength.score !== undefined ? `:${parts.strength.score}` : ''}`,
        ...parts.coreInteractions.map((interaction: string) => `interaction:${interaction}`),
        ...parts.majorStars.map((star: string) => `star:${star}`),
        parts.majorLuck ? `majorLuck:${parts.majorLuck.direction}:${parts.majorLuck.startAge}:${parts.majorLuck.basisTerm}` : '',
        `timeUnknown:${parts.timeUnknown ? 'true' : 'false'}`
    ].filter((item: string) => !!item));
}

function findSignal(signals: InterpretationSignal[], signalId: string) {
    return (signals || []).find((signal: InterpretationSignal) => signal.signalId === signalId);
}

function starEntry(group: string, star: any) {
    if (!star?.name) return '';
    return `${group}:${star.name}@${star.location || star.target || '-'}`;
}

function formatElements(elements: ChartSignatureElement[]) {
    return elements.length
        ? elements.map((item: ChartSignatureElement) => `${item.label}:${item.value}:${item.percent}`).join(',')
        : 'none';
}

function textValue(value: any) {
    return value === undefined || value === null || value === '' ? '미정' : String(value);
}

function numberOrUndefined(value: any) {
    let numberValue = Number(value);
    return Number.isNaN(numberValue) ? undefined : numberValue;
}

function roundOne(value: number) {
    return Math.round(value * 10) / 10;
}

function elementSort(a: ChartSignatureElement, b: ChartSignatureElement) {
    return ELEMENT_LABELS.indexOf(a.label) - ELEMENT_LABELS.indexOf(b.label);
}

function unique(values: string[]) {
    let seen: any = {};
    return values.filter((value: string) => {
        if (seen[value]) return false;
        seen[value] = true;
        return true;
    });
}

function stableHash(value: string) {
    let hash = 2166136261;
    for (let i = 0; i < value.length; i++) {
        hash ^= value.charCodeAt(i);
        hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0).toString(36);
}
